import {
  // Navigation
  HomeIcon,
  CalendarIcon,
  PlusIcon,
  UserGroupIcon,
  // Actions
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  // Time/Date
  ClockIcon,
  MapPinIcon,
  // Settings
  Cog6ToothIcon,
  // Menu
  Bars3Icon,
  // Weather
  CloudIcon,
  // Status
  ExclamationTriangleIcon,
  CheckCircleIcon,
  // Event Types
  GiftIcon as CakeIcon,
  HeartIcon,
  HandRaisedIcon,
  FireIcon as SparklesIcon,
  AcademicCapIcon,
  BuildingOfficeIcon as BuildingOffice2Icon,
  StarIcon,
  // Time of Day
  SunIcon,
  MoonIcon,
  // Family
  LockClosedIcon,
  ArrowRightOnRectangleIcon,
  // Lists/Todos
  ClipboardDocumentListIcon,
  // Filter
  FunnelIcon,
  // Chevron
  ChevronDownIcon,
  ChevronUpIcon,
  // AI/Discovery
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  CalendarIcon as CalendarIconSolid,
  PlusIcon as PlusIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
} from '@heroicons/react/24/solid';

// Icon name type for type safety
export type IconName =
  | 'home'
  | 'calendar'
  | 'plus'
  | 'family'
  | 'edit'
  | 'delete'
  | 'check'
  | 'close'
  | 'arrow-left'
  | 'arrow-right'
  | 'arrow-up'
  | 'arrow-down'
  | 'clock'
  | 'location'
  | 'settings'
  | 'menu'
  | 'weather'
  | 'warning'
  | 'success'
  | 'birthday'
  | 'anniversary'
  | 'visit'
  | 'holiday'
  | 'school'
  | 'medical'
  | 'milestone'
  | 'sun'
  | 'moon'
  | 'lock'
  | 'sign-out'
  | 'checklist'
  | 'filter'
  | 'chevron-down'
  | 'chevron-up'
  | 'whats-new'
  | 'discoveries';

// Icon component props
export interface IconProps {
  name: IconName;
  className?: string;
  solid?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

// Size mapping
const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8',
};

// Icon mapping
const iconMap = {
  home: HomeIcon,
  calendar: CalendarIcon,
  plus: PlusIcon,
  family: UserGroupIcon,
  edit: PencilIcon,
  delete: TrashIcon,
  check: CheckIcon,
  close: XMarkIcon,
  'arrow-left': ArrowLeftIcon,
  'arrow-right': ArrowRightIcon,
  'arrow-up': ArrowUpIcon,
  'arrow-down': ArrowDownIcon,
  clock: ClockIcon,
  location: MapPinIcon,
  settings: Cog6ToothIcon,
  menu: Bars3Icon,
  weather: CloudIcon,
  warning: ExclamationTriangleIcon,
  success: CheckCircleIcon,
  birthday: CakeIcon,
  anniversary: HeartIcon,
  visit: HandRaisedIcon,
  holiday: SparklesIcon,
  school: AcademicCapIcon,
  medical: BuildingOffice2Icon,
  milestone: StarIcon,
  sun: SunIcon,
  moon: MoonIcon,
  lock: LockClosedIcon,
  'sign-out': ArrowRightOnRectangleIcon,
  checklist: ClipboardDocumentListIcon,
  filter: FunnelIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-up': ChevronUpIcon,
  'whats-new': SparklesIcon,
  discoveries: LightBulbIcon,
};

// Solid icon mapping (for active states)
const solidIconMap = {
  home: HomeIconSolid,
  calendar: CalendarIconSolid,
  plus: PlusIconSolid,
  family: UserGroupIconSolid,
  checklist: ClipboardDocumentListIconSolid,
};

/**
 * Icon component that renders Heroicons based on icon name
 * @example
 * <Icon name="home" size="lg" />
 * <Icon name="calendar" solid className="text-teal-600" />
 */
export function Icon({ name, className = '', solid = false, size = 'md' }: IconProps) {
  const IconComponent = solid && solidIconMap[name as keyof typeof solidIconMap]
    ? solidIconMap[name as keyof typeof solidIconMap]
    : iconMap[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found`);
    return null;
  }

  const sizeClass = sizeClasses[size];
  const combinedClassName = `${sizeClass} ${className}`.trim();

  return <IconComponent className={combinedClassName} />;
}

/**
 * Get icon component by name (for advanced usage)
 */
export function getIconComponent(name: IconName, solid = false) {
  if (solid && solidIconMap[name as keyof typeof solidIconMap]) {
    return solidIconMap[name as keyof typeof solidIconMap];
  }
  return iconMap[name];
}
